/**
 * Dashboard Page
 * Main dashboard with overview and quick actions
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../hooks/useAuth'
import { dashboardService } from '../services/dashboard.service'
import { botsService } from '../services/bots.service'
import Layout from '../components/Layout'
import { EmailVerificationBanner } from '../components/email-verification/EmailVerificationBanner'

export default function DashboardPage() {
  const { user } = useAuth()
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false)

  // Fetch dashboard overview
  const { data: overview, isLoading, refetch: refetchOverview } = useQuery({
    queryKey: ['dashboard', 'overview'],
    queryFn: () => dashboardService.getOverview(),
    refetchInterval: 30000, // Refresh every 30 seconds
  })

  // Fetch health status
  const { data: health } = useQuery({
    queryKey: ['dashboard', 'health'],
    queryFn: () => dashboardService.getHealth(),
    refetchInterval: 60000, // Refresh every minute
  })

  // Handle refresh all active bots
  const handleRefreshAll = async () => {
    if (isRefreshing) return

    setIsRefreshing(true)
    try {
      const result = await botsService.refreshAll()
      await refetchOverview()

      if (result.success) {
        alert(`成功刷新 ${result.refreshed} 個 Bot`)
      } else {
        alert(`刷新完成：${result.refreshed} 個成功，${result.errors} 個失敗`)
      }
    } catch (error) {
      console.error('Failed to refresh bots:', error)
      alert('刷新失敗，請稍後再試')
    } finally {
      setIsRefreshing(false)
    }
  }

  return (
    <Layout>
      <div style={styles.container}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>儀表板</h1>
            <p style={styles.subtitle}>歡迎回來，{user?.email}</p>
          </div>
          {overview && overview.data?.bots?.active > 0 && (
            <button onClick={handleRefreshAll} disabled={isRefreshing} style={styles.refreshButton}>
              {isRefreshing ? '刷新中...' : '🔄 刷新所有 Bot'}
            </button>
          )}
        </div>

        {/* Email Verification Banner - T024: Only show when user is logged in AND email_verified = false */}
        {user && user.email && user.email_verified === false && (
          <EmailVerificationBanner email={user.email} />
        )}

        {/* Stats Cards */}
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>🔑</div>
            <div style={styles.statContent}>
              <p style={styles.statLabel}>API Keys</p>
              <p style={styles.statValue}>
                {isLoading ? '-' : overview?.data?.apiKeys?.active || 0}
              </p>
              <p style={styles.statSubtext}>
                {isLoading ? '載入中...' : `${overview?.data?.apiKeys?.total || 0} 總計`}
              </p>
            </div>
          </div>

          <div style={styles.statCard}>
            <div style={styles.statIcon}>🤖</div>
            <div style={styles.statContent}>
              <p style={styles.statLabel}>Bot 實例</p>
              <p style={styles.statValue}>
                {isLoading ? '-' : `${overview?.data?.bots?.active || 0}/${overview?.data?.bots?.total || 0}`}
              </p>
              <p style={styles.statSubtext}>
                {overview?.data?.bots?.active === 0 ? '尚無運行中的 Bot' : `${overview?.data?.bots?.active} 運行中`}
              </p>
            </div>
          </div>

          <div style={styles.statCard}>
            <div style={styles.statIcon}>📊</div>
            <div style={styles.statContent}>
              <p style={styles.statLabel}>總收益</p>
              <p style={styles.statValue}>
                {isLoading ? '-' : `$${(overview?.data?.financial?.totalEarned || 0).toFixed(4)}`}
              </p>
              <p style={styles.statSubtext}>
                總放貸: ${(overview?.data?.financial?.totalLent || 0).toFixed(2)}
              </p>
            </div>
          </div>

          <div style={styles.statCard}>
            <div style={styles.statIcon}>📈</div>
            <div style={styles.statContent}>
              <p style={styles.statLabel}>平均年化收益</p>
              <p style={styles.statValue}>
                {isLoading ? '-' : `${(overview?.data?.financial?.averageAPR || 0).toFixed(2)}%`}
              </p>
              <p style={styles.statSubtext}>
                活躍掛單: {overview?.data?.activity?.activeOffers || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>快速操作</h2>
          <div style={styles.actionsGrid}>
            <Link to="/api-keys" style={styles.actionCard}>
              <div style={styles.actionIcon}>🔑</div>
              <div>
                <h3 style={styles.actionTitle}>管理 API Keys</h3>
                <p style={styles.actionDescription}>
                  創建、查看或撤銷 API Keys 以進行程式化存取
                </p>
              </div>
            </Link>

            <Link to="/bots" style={styles.actionCard}>
              <div style={styles.actionIcon}>🤖</div>
              <div>
                <h3 style={styles.actionTitle}>配置 Bot</h3>
                <p style={styles.actionDescription}>
                  設定自動放貸策略並啟動 Bot 實例
                </p>
              </div>
            </Link>

            <Link to="/reports" style={styles.actionCard}>
              <div style={styles.actionIcon}>📈</div>
              <div>
                <h3 style={styles.actionTitle}>查看報表</h3>
                <p style={styles.actionDescription}>
                  檢視收益統計和歷史放貸記錄
                </p>
              </div>
            </Link>
          </div>
        </div>

        {/* Getting Started */}
        {overview && overview.data?.apiKeys?.active === 0 && (
          <div style={styles.section}>
            <div style={styles.gettingStarted}>
              <h2 style={styles.gettingStartedTitle}>🚀 開始使用</h2>
              <p style={styles.gettingStartedText}>
                歡迎使用 CyberLoan.ai！要開始使用自動放貸功能，請先創建您的第一個 API Key。
              </p>
              <ol style={styles.stepsList}>
                <li style={styles.stepItem}>
                  <strong>創建 API Key</strong> - 前往 API Keys 頁面創建新的存取金鑰
                </li>
                <li style={styles.stepItem}>
                  <strong>設定 Bitfinex API</strong> - 在您的帳戶中設定 Bitfinex API 憑證
                </li>
                <li style={styles.stepItem}>
                  <strong>配置 Bot</strong> - 設定放貸策略並啟動您的第一個 Bot
                </li>
                <li style={styles.stepItem}>
                  <strong>監控收益</strong> - 在報表頁面追蹤您的放貸收益
                </li>
              </ol>
              <Link to="/api-keys" style={styles.getStartedButton}>
                立即開始
              </Link>
            </div>
          </div>
        )}

        {/* Wallet Balances */}
        {overview && overview.data?.wallets && overview.data.wallets.length > 0 && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>錢包餘額</h2>
            <div style={styles.walletsGrid}>
              {overview.data.wallets.map((wallet) => {
                const totalAssets = wallet.balance + wallet.lent
                const utilization = totalAssets > 0 ? (wallet.lent / totalAssets) * 100 : 0

                return (
                  <div key={wallet.currency} style={styles.walletCard}>
                    <div style={styles.walletHeader}>
                      <span style={styles.walletCurrency}>{wallet.currency}</span>
                      <span style={styles.walletUtilization}>
                        {utilization.toFixed(1)}% 利用
                      </span>
                    </div>
                    <div style={styles.walletBalance}>
                      {totalAssets.toFixed(2)}
                    </div>
                    <div style={styles.walletBalanceLabel}>總資產</div>
                    <div style={styles.walletDetails}>
                      <div style={styles.walletDetailItem}>
                        <span style={styles.walletDetailLabel}>已貸出:</span>
                        <span style={styles.walletDetailValue}>{wallet.lent.toFixed(2)}</span>
                      </div>
                      <div style={styles.walletDetailItem}>
                        <span style={styles.walletDetailLabel}>可用:</span>
                        <span style={styles.walletDetailValue}>
                          {wallet.balance.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Recent Activities */}
        {overview && overview.data?.recentActivities && overview.data.recentActivities.length > 0 && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>最近活動</h2>
            <div style={styles.activitiesContainer}>
              {overview.data.recentActivities.map((activity, index) => (
                <div key={`${activity.bot_id}-${index}`} style={styles.activityItem}>
                  <div style={styles.activityIcon}>
                    {activity.status === 'active' ? '🟢' : activity.status === 'error' ? '🔴' : '⚪'}
                  </div>
                  <div style={styles.activityContent}>
                    <div style={styles.activityTitle}>
                      {activity.bot_name} ({activity.currency})
                    </div>
                    <div style={styles.activityMeta}>
                      {activity.last_cycle_at
                        ? `最後執行: ${new Date(activity.last_cycle_at).toLocaleString('zh-TW')}`
                        : '尚未執行'}
                    </div>
                    {activity.error_message && (
                      <div style={styles.activityError}>{activity.error_message}</div>
                    )}
                  </div>
                  <div style={styles.activityStatus}>
                    <Link to={`/bots/${activity.bot_id}/monitor`} style={styles.activityLink}>
                      查看 →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* System Status */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>系統狀態</h2>
          <div style={styles.statusContainer}>
            <div style={styles.statusItem}>
              <span style={styles.statusIndicator}>
                {health?.services.api === 'healthy' ? '🟢' : '🔴'}
              </span>
              <span style={styles.statusText}>
                API 服務{health?.services.api === 'healthy' ? '正常' : '異常'}
              </span>
            </div>
            <div style={styles.statusItem}>
              <span style={styles.statusIndicator}>
                {health?.services.database === 'healthy' ? '🟢' : '🔴'}
              </span>
              <span style={styles.statusText}>
                資料庫連線{health?.services.database === 'healthy' ? '正常' : '異常'}
              </span>
            </div>
            <div style={styles.statusItem}>
              <span style={styles.statusIndicator}>
                {health?.services.cache === 'healthy' ? '🟢' : '🔴'}
              </span>
              <span style={styles.statusText}>
                快取服務{health?.services.cache === 'healthy' ? '正常' : '異常'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '40px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '40px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '16px',
    color: '#666',
  },
  refreshButton: {
    padding: '12px 24px',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '40px',
  },
  statCard: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '24px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  statIcon: {
    fontSize: '32px',
  },
  statContent: {
    flex: 1,
  },
  statLabel: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '4px',
    textTransform: 'uppercase',
    fontWeight: '500',
  },
  statValue: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '4px',
  },
  statSubtext: {
    fontSize: '12px',
    color: '#999',
  },
  section: {
    marginBottom: '40px',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '20px',
  },
  actionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '20px',
  },
  actionCard: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '24px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    textDecoration: 'none',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.2s, box-shadow 0.2s',
    cursor: 'pointer',
  },
  actionIcon: {
    fontSize: '32px',
  },
  actionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '6px',
  },
  actionDescription: {
    fontSize: '14px',
    color: '#666',
    lineHeight: '1.5',
  },
  gettingStarted: {
    backgroundColor: '#eff6ff',
    borderRadius: '10px',
    padding: '30px',
    border: '2px dashed #2563eb',
  },
  gettingStartedTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#2563eb',
    marginBottom: '16px',
  },
  gettingStartedText: {
    fontSize: '15px',
    color: '#1e40af',
    marginBottom: '20px',
    lineHeight: '1.6',
  },
  stepsList: {
    marginLeft: '20px',
    marginBottom: '24px',
    color: '#1e40af',
  },
  stepItem: {
    fontSize: '14px',
    marginBottom: '12px',
    lineHeight: '1.6',
  },
  getStartedButton: {
    display: 'inline-block',
    padding: '12px 24px',
    backgroundColor: '#2563eb',
    color: 'white',
    borderRadius: '5px',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'background-color 0.2s',
  },
  statusContainer: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  statusItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  statusIndicator: {
    fontSize: '12px',
  },
  statusText: {
    fontSize: '14px',
    color: '#333',
  },
  // Wallet styles
  walletsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
  },
  walletCard: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  walletHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  walletCurrency: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333',
  },
  walletUtilization: {
    fontSize: '11px',
    color: '#10b981',
    backgroundColor: '#d1fae5',
    padding: '2px 8px',
    borderRadius: '4px',
  },
  walletBalance: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '4px',
  },
  walletBalanceLabel: {
    fontSize: '12px',
    color: '#999',
    marginBottom: '12px',
  },
  walletDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  walletDetailItem: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '12px',
  },
  walletDetailLabel: {
    color: '#666',
  },
  walletDetailValue: {
    color: '#333',
    fontWeight: '500',
  },
  // Activity styles
  activitiesContainer: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '16px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  activityItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '12px 0',
    borderBottom: '1px solid #f3f4f6',
  },
  activityIcon: {
    fontSize: '16px',
    marginTop: '2px',
  },
  activityContent: {
    flex: 1,
  },
  activityTitle: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#333',
    marginBottom: '4px',
  },
  activityMeta: {
    fontSize: '12px',
    color: '#666',
  },
  activityError: {
    fontSize: '12px',
    color: '#ef4444',
    marginTop: '4px',
  },
  activityStatus: {
    marginTop: '2px',
  },
  activityLink: {
    fontSize: '13px',
    color: '#2563eb',
    textDecoration: 'none',
    fontWeight: '500',
  },
}
